import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, UploadCloud, CheckCircle2, ArrowRight, Clock, FileCheck2, Camera } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Reveal, Overline } from "./Reveal";
import { PHONE, PHONE_HREF, SERVICE_OPTIONS } from "@/lib/site-data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_METHODS = ["Call", "Text", "Email"];

const inputCls =
  "w-full rounded-md border border-white/15 bg-white/5 px-4 py-3.5 text-sm text-white placeholder-gray-500 outline-none transition-[border-color,box-shadow] duration-200 focus:border-cyan-400 focus:shadow-[0_0_15px_rgba(0,229,255,0.15)]";

const EMPTY = { firstName: "", lastName: "", phone: "", email: "", address: "", service: "", description: "", contactMethod: "Call" };

export default function EstimateForm() {
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, service: e.detail }));
    window.addEventListener("prefill-service", handler);
    return () => window.removeEventListener("prefill-service", handler);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.phone || !form.email || !form.service) {
      toast.error("Please fill in name, phone, email, and the service you need.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("first_name", form.firstName);
      fd.append("last_name", form.lastName);
      fd.append("phone", form.phone);
      fd.append("email", form.email);
      fd.append("address", form.address);
      fd.append("service", form.service);
      fd.append("description", form.description);
      fd.append("contact_method", form.contactMethod);
      photos.forEach((p) => fd.append("photos", p));
      const res = await axios.post(`${API}/estimates`, fd);
      setSubmitted(res.data.estimate_id);
      toast.success("Estimate request sent. We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong sending your request. Please call us at (502) 963-2314.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="estimate" data-testid="estimate-section" className="relative bg-[#111827] py-28 md:py-36 overflow-hidden">
      <div className="absolute -top-24 right-0 h-[380px] w-[380px] rounded-full bg-[#0066FF]/20 blur-[120px]" />
      <div className="absolute bottom-0 -left-24 h-[300px] w-[300px] rounded-full bg-[#00E5FF]/10 blur-[110px]" />

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-[1fr_1.15fr] gap-16">
        <Reveal>
          <Overline>05 — Free Estimate</Overline>
          <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">
            Let's Fix the Problem.
          </h2>
          <p className="mt-5 text-gray-400 max-w-md leading-relaxed">
            Tell us what you're dealing with and our team will reach out to discuss the next step.
          </p>

          <a
            href={PHONE_HREF}
            data-testid="estimate-call-card"
            className="mt-10 glass-dark rounded-2xl p-6 flex items-center gap-5 group hover:border-cyan-400/40 transition-colors duration-300"
          >
            <span className="rounded-xl bg-[#0066FF]/20 p-4">
              <Phone size={26} className="text-cyan-400" />
            </span>
            <span>
              <span className="block text-xs uppercase tracking-widest text-gray-400">Prefer to talk?</span>
              <span className="block font-display text-2xl font-semibold mt-1 group-hover:text-cyan-400 transition-colors duration-300">
                {PHONE}
              </span>
            </span>
          </a>

          <ul className="mt-10 space-y-4">
            {[
              { icon: Clock, text: "Response within one business day" },
              { icon: FileCheck2, text: "Free, no-obligation estimates" },
              { icon: Camera, text: "Photos help us quote faster" },
            ].map((b) => (
              <li key={b.text} className="flex items-center gap-3 text-gray-300 text-sm">
                <b.icon size={18} className="text-cyan-400" /> {b.text}
              </li>
            ))}
          </ul>

          <div className="mt-10 rounded-2xl overflow-hidden border border-white/10">
            <img
              src="https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&q=80&w=900"
              alt="Finished Phillips project — Kentucky home"
              loading="lazy"
              className="h-52 w-full object-cover"
            />
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="glass-dark rounded-3xl p-7 md:p-10">
            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="done"
                  data-testid="estimate-confirmation"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="text-center py-14"
                >
                  <CheckCircle2 size={64} className="mx-auto text-cyan-400 drop-shadow-[0_0_20px_rgba(0,229,255,0.6)]" />
                  <h3 className="font-display text-3xl font-semibold mt-6">Request received.</h3>
                  <p className="mt-3 text-gray-400 max-w-sm mx-auto text-sm leading-relaxed">
                    Thanks — our team will review your project and reach out shortly. Reference{" "}
                    <span className="text-cyan-400 font-mono text-xs">{submitted.slice(0, 8).toUpperCase()}</span>
                  </p>
                  <button
                    data-testid="estimate-another-btn"
                    onClick={() => {
                      setSubmitted(null);
                      setForm(EMPTY);
                      setPhotos([]);
                    }}
                    className="mt-8 inline-flex items-center gap-2 rounded-md border border-white/20 px-6 py-3 text-sm font-bold text-white hover:border-cyan-400 hover:text-cyan-400 transition-colors duration-200"
                  >
                    Submit another request <ArrowRight size={15} />
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  data-testid="estimate-form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onSubmit={submit}
                  className="grid sm:grid-cols-2 gap-4"
                >
                  <input data-testid="estimate-first-name" required placeholder="First Name *" value={form.firstName} onChange={set("firstName")} className={inputCls} />
                  <input data-testid="estimate-last-name" required placeholder="Last Name *" value={form.lastName} onChange={set("lastName")} className={inputCls} />
                  <input data-testid="estimate-phone" required type="tel" placeholder="Phone Number *" value={form.phone} onChange={set("phone")} className={inputCls} />
                  <input data-testid="estimate-email" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} />
                  <input data-testid="estimate-address" placeholder="Property Address" value={form.address} onChange={set("address")} className={`${inputCls} sm:col-span-2`} />

                  <div className="sm:col-span-2">
                    <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                      <SelectTrigger data-testid="estimate-service-select" className="w-full rounded-md border border-white/15 bg-white/5 px-4 py-3.5 text-sm text-white">
                        <SelectValue placeholder="Service Needed *" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#111827] border-white/15 text-white">
                        {SERVICE_OPTIONS.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`estimate-service-option-${s.toLowerCase().replace(/\s+/g, "-")}`} className="focus:bg-[#0066FF]/30 focus:text-white">
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <textarea
                    data-testid="estimate-description"
                    placeholder="Describe the problem — what are you seeing, and how long has it been happening?"
                    value={form.description}
                    onChange={set("description")}
                    rows={4}
                    className={`${inputCls} sm:col-span-2 resize-none`}
                  />

                  <div className="sm:col-span-2">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      data-testid="estimate-photo-input"
                      onChange={(e) => setPhotos(Array.from(e.target.files || []).slice(0, 5))}
                    />
                    <button
                      type="button"
                      data-testid="estimate-photo-upload-btn"
                      onClick={() => fileRef.current?.click()}
                      className="w-full rounded-md border-2 border-dashed border-white/20 px-4 py-6 text-sm text-gray-400 flex items-center justify-center gap-3 transition-[border-color,color] duration-200 hover:border-cyan-400/60 hover:text-cyan-400"
                    >
                      <UploadCloud size={20} />
                      {photos.length ? `${photos.length} photo${photos.length > 1 ? "s" : ""} attached` : "Upload photos of the problem (optional, up to 5)"}
                    </button>
                  </div>

                  <div className="sm:col-span-2">
                    <p className="text-xs uppercase tracking-widest text-gray-400 font-bold mb-2.5">Preferred Contact Method</p>
                    <div className="flex gap-2">
                      {CONTACT_METHODS.map((m) => (
                        <button
                          key={m}
                          type="button"
                          data-testid={`estimate-contact-${m.toLowerCase()}`}
                          onClick={() => setForm((f) => ({ ...f, contactMethod: m }))}
                          className={`rounded-full px-5 py-2 text-sm font-bold transition-[background-color,color] duration-200 ${
                            form.contactMethod === m ? "bg-[#0066FF] text-white" : "bg-white/5 text-gray-400 hover:text-white"
                          }`}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    type="submit"
                    data-testid="estimate-submit-btn"
                    disabled={submitting}
                    className="sm:col-span-2 mt-2 inline-flex items-center justify-center gap-2 rounded-md bg-[#0066FF] px-8 py-4 font-display text-sm font-semibold tracking-widest text-white transition-[background-color,box-shadow,color] duration-300 hover:bg-[#00E5FF] hover:text-black hover:shadow-[0_0_35px_rgba(0,229,255,0.55)] disabled:opacity-60"
                  >
                    {submitting ? "SENDING…" : "GET MY FREE ESTIMATE"} <ArrowRight size={16} />
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
